import React, { createContext, useState, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Snackbar, Alert, Box } from '@mui/material';
import { NOTIFICATION_TIMEOUT } from '../common/AppConstants';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info', timeout) => {
    const finalTimeout =
      timeout || NOTIFICATION_TIMEOUT[type] || NOTIFICATION_TIMEOUT.info;
    const newNotification = {
      id: uuidv4(),
      message,
      type,
      open: true,
      timeout: finalTimeout,
    };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const removeNotification = (id) => {
    setNotifications((prev) =>
      prev.map(
        (notif) => (notif.id === id ? { ...notif, open: false } : notif), // Close the specific notification
      ),
    );

    // Remove from array after animation completes
    setTimeout(() => {
      setNotifications((prev) => prev.filter(notif => notif.id !== id));
    }, 500); // Adjust based on MUI exit animation duration
  };

  return (
    <NotificationContext.Provider
      value={{ addNotification, removeNotification }}
    >
      {children}
      <Box className="notification-container">
        {notifications.map((notif, index) => (
          <Notification
            key={notif.id}
            message={notif.message}
            type={notif.type}
            timeout={notif.timeout}
            open={notif.open}
            onClose={() => removeNotification(notif.id)}
            index={index}
          />
        ))}
      </Box>
    </NotificationContext.Provider>
  );
};

// Notification component can be a custom MUI Snackbar, Alert, etc.
const Notification = ({ message, type, timeout, open, onClose, index }) => (
  <Snackbar
    open={open}
    autoHideDuration={timeout} // Automatically hide after 5 seconds
    onClose={onClose}
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    sx={{
      mt: '64px' ,
      transform: `translateY(${index * 60}px)`, // Stack notifications with increasing margin
      mr: 2, // Fixed margin from the right
    }}
  >
    <Alert
      onClose={onClose}
      severity={type} // Can be 'error', 'warning', 'info', 'success'
      sx={{ width: '100%' }}
    >
      {message}
    </Alert>
  </Snackbar>
);
